import React, { useState } from "react";
import { PieChart, ShieldCheck, AlertTriangle, HeartPulse, Sparkles, TrendingUp } from "lucide-react";
import WidgetInfoTooltip from "./WidgetInfoTooltip";

export default function MacroHealthDistributionCard({ metrics, totalEmployees = 0 }) {
  const [hoveredTier, setHoveredTier] = useState(null);

  const macro = metrics?.macroDistribution || {
    apparentlyHealthy: {
      count: 0,
      percent: 25,
      label: "Aparentemente Sanos",
      sublabel: "0 factores de riesgo detectados",
      color: "#10B981",
      bg: "#ECFDF5",
      border: "#A7F3D0",
      action: "Fidelización en hábitos de bienestar preventivo y resiliencia biológica.",
    },
    withRiskFactors: {
      count: 0,
      percent: 65,
      label: "Con Factores de Riesgo",
      sublabel: "1 a 2 factores modificables",
      color: "#F59E0B",
      bg: "#FFFBEB",
      border: "#FDE68A",
      action: "Intervención temprana, coaching digital con IA y prevención primaria.",
    },
    withDisease: {
      count: 0,
      percent: 10,
      label: "Con Enfermedad / Patología Activa",
      sublabel: "3 o más factores concurrentes",
      color: "#EF4444",
      bg: "#FEF2F2",
      border: "#FECACA",
      action: "Derivación prioritaria a valoración médica y seguimiento asistencial.",
    },
  };

  const { apparentlyHealthy, withRiskFactors, withDisease } = macro;
  const sampleCount = metrics?.sampleSize || totalEmployees || 1;

  // Cálculo SVG Donut Arcs
  const radius = 68;
  const strokeWidth = 22;
  const circumference = 2 * Math.PI * radius;

  // Normalizar porcentajes para que sumen 100 en el gráfico
  const rawTotal = (apparentlyHealthy.percent + withRiskFactors.percent + withDisease.percent) || 100;
  const normHealthy = (apparentlyHealthy.percent / rawTotal) * 100;
  const normRisk = (withRiskFactors.percent / rawTotal) * 100;
  const normDisease = (withDisease.percent / rawTotal) * 100;

  const healthyDash = (normHealthy / 100) * circumference;
  const riskDash = (normRisk / 100) * circumference;
  const diseaseDash = (normDisease / 100) * circumference;

  const healthyOffset = 0;
  const riskOffset = -healthyDash;
  const diseaseOffset = -(healthyDash + riskDash);

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm relative overflow-hidden"
      data-testid="macro-health-distribution-card"
    >
      {/* Cabecera del Componente */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
              <PieChart size={12} />
              Vigilancia Epidemiológica Macro
            </span>
            <span className="text-xs text-slate-400 font-medium">
              Muestra evaluada: <strong>{sampleCount}</strong> colaboradores
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <h3 className="text-base font-bold text-slate-900">
              Distribución Tripartita de Salud Poblacional
            </h3>
            <WidgetInfoTooltip
              title="Distribución Tripartita de Salud Poblacional (Macro-Estratificación)"
              align="left"
              what="Estratificación ejecutiva de la fuerza laboral en 3 macro-grupos clínicos de acuerdo a la carga acumulada de factores de riesgo modificables."
              calculation="• Aparentemente Sanos: 0 de 6 factores de corte presentes (WHtR ≤ 0.50, PA < 130/85, IMC < 25, Glicemia < 100, etc.). • Con Factores de Riesgo: 1 a 2 factores incipientes o reversibles. • Con Enfermedad / Patología Activa: 3 o más factores concurrentes (clúster metabólico) o patología activa declarada."
              standard="Marco de Vigilancia Epidemiológica de Enfermedades Crónicas OMS, NCEP ATP III & OIT C161."
              relevance="Permite al Directorio y al Comité de SST disponer de un paneo instantáneo del perfil de vulnerabilidad para evaluar el Retorno de Inversión (ROI) preventivo y monitorear la migración progresiva del grupo de riesgo hacia el grupo saludable."
            />
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Visión global ejecutiva solicitada por Dirección Médica: Aparentemente Sanos vs. En Riesgo vs. Patología Activa.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/60 text-xs text-slate-600">
          <TrendingUp size={14} className="text-emerald-600" />
          <span>Meta Preventiva: <strong>≥ 40%</strong> Aparentemente Sanos</span>
        </div>
      </div>

      {/* Contenido Principal: Donut Chart + 3 Tarjetas de Estado */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        {/* Gráfico Donut SVG Central */}
        <div className="lg:col-span-4 flex flex-col items-center justify-center p-3 bg-slate-50/70 rounded-2xl border border-slate-100">
          <div className="relative w-48 h-48 flex items-center justify-center">
            <svg
              className="w-full h-full -rotate-90 transform"
              viewBox="0 0 180 180"
              aria-label="Gráfico de distribución de salud poblacional"
            >
              {/* Círculo base */}
              <circle
                cx="90"
                cy="90"
                r={radius}
                className="stroke-slate-200/60"
                strokeWidth={strokeWidth}
                fill="transparent"
              />

              {/* Segmento: Con Enfermedad (Rojo) */}
              <circle
                cx="90"
                cy="90"
                r={radius}
                stroke={withDisease.color}
                strokeWidth={hoveredTier === "disease" ? strokeWidth + 4 : strokeWidth}
                strokeDasharray={`${diseaseDash} ${circumference - diseaseDash}`}
                strokeDashoffset={diseaseOffset}
                strokeLinecap="round"
                fill="transparent"
                className="transition-all duration-300 cursor-pointer"
                onMouseEnter={() => setHoveredTier("disease")}
                onMouseLeave={() => setHoveredTier(null)}
              />

              {/* Segmento: Con Factores de Riesgo (Ámbar) */}
              <circle
                cx="90"
                cy="90"
                r={radius}
                stroke={withRiskFactors.color}
                strokeWidth={hoveredTier === "risk" ? strokeWidth + 4 : strokeWidth}
                strokeDasharray={`${riskDash} ${circumference - riskDash}`}
                strokeDashoffset={riskOffset}
                strokeLinecap="round"
                fill="transparent"
                className="transition-all duration-300 cursor-pointer"
                onMouseEnter={() => setHoveredTier("risk")}
                onMouseLeave={() => setHoveredTier(null)}
              />

              {/* Segmento: Aparentemente Sanos (Verde) */}
              <circle
                cx="90"
                cy="90"
                r={radius}
                stroke={apparentlyHealthy.color}
                strokeWidth={hoveredTier === "healthy" ? strokeWidth + 4 : strokeWidth}
                strokeDasharray={`${healthyDash} ${circumference - healthyDash}`}
                strokeDashoffset={healthyOffset}
                strokeLinecap="round"
                fill="transparent"
                className="transition-all duration-300 cursor-pointer"
                onMouseEnter={() => setHoveredTier("healthy")}
                onMouseLeave={() => setHoveredTier(null)}
              />
            </svg>

            {/* Centro Informativo del Donut */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
              <span className="text-2xl font-black text-slate-900 tracking-tight">
                {hoveredTier === "healthy"
                  ? `${apparentlyHealthy.percent}%`
                  : hoveredTier === "risk"
                  ? `${withRiskFactors.percent}%`
                  : hoveredTier === "disease"
                  ? `${withDisease.percent}%`
                  : `${apparentlyHealthy.percent}%`}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 max-w-[90px] leading-tight">
                {hoveredTier === "healthy"
                  ? "Sanos"
                  : hoveredTier === "risk"
                  ? "En Riesgo"
                  : hoveredTier === "disease"
                  ? "Con Patología"
                  : "Sanos"}
              </span>
              <span className="text-[9px] text-slate-400 mt-0.5">
                {sampleCount} colaboradores
              </span>
            </div>
          </div>

          <p className="text-[11px] text-slate-500 mt-3 text-center">
            Pase el cursor sobre los arcos para aislar cada grupo
          </p>
        </div>

        {/* 3 Tarjetas Detalladas Bento */}
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          {/* Card 1: Aparentemente Sanos */}
          <div
            onMouseEnter={() => setHoveredTier("healthy")}
            onMouseLeave={() => setHoveredTier(null)}
            className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
              hoveredTier === "healthy"
                ? "ring-2 ring-emerald-500 border-emerald-300 bg-emerald-50/70 shadow-sm"
                : "border-slate-200/80 bg-white hover:bg-slate-50/70"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <ShieldCheck size={18} />
              </div>
              <span className="text-xs font-bold text-emerald-800 bg-emerald-100/70 px-2 py-0.5 rounded-full">
                {apparentlyHealthy.percent}%
              </span>
            </div>
            <div className="text-xl font-extrabold text-slate-900">
              {apparentlyHealthy.count}
              <span className="text-xs font-normal text-slate-400 ml-1">colaboradores</span>
            </div>
            <h4 className="text-xs font-bold text-slate-800 mt-1">
              {apparentlyHealthy.label}
            </h4>
            <p className="text-[11px] text-slate-500 mb-2">
              {apparentlyHealthy.sublabel}
            </p>
            <div className="text-[10px] text-emerald-800 font-medium bg-emerald-50/80 p-2 rounded-lg border border-emerald-100 leading-snug">
              {apparentlyHealthy.action}
            </div>
          </div>

          {/* Card 2: Con Factores de Riesgo */}
          <div
            onMouseEnter={() => setHoveredTier("risk")}
            onMouseLeave={() => setHoveredTier(null)}
            className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
              hoveredTier === "risk"
                ? "ring-2 ring-amber-500 border-amber-300 bg-amber-50/70 shadow-sm"
                : "border-slate-200/80 bg-white hover:bg-slate-50/70"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                <AlertTriangle size={18} />
              </div>
              <span className="text-xs font-bold text-amber-800 bg-amber-100/70 px-2 py-0.5 rounded-full">
                {withRiskFactors.percent}%
              </span>
            </div>
            <div className="text-xl font-extrabold text-slate-900">
              {withRiskFactors.count}
              <span className="text-xs font-normal text-slate-400 ml-1">colaboradores</span>
            </div>
            <h4 className="text-xs font-bold text-slate-800 mt-1">
              {withRiskFactors.label}
            </h4>
            <p className="text-[11px] text-slate-500 mb-2">
              {withRiskFactors.sublabel}
            </p>
            <div className="text-[10px] text-amber-800 font-medium bg-amber-50/80 p-2 rounded-lg border border-amber-100 leading-snug">
              {withRiskFactors.action}
            </div>
          </div>

          {/* Card 3: Con Enfermedad / Patología Activa */}
          <div
            onMouseEnter={() => setHoveredTier("disease")}
            onMouseLeave={() => setHoveredTier(null)}
            className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
              hoveredTier === "disease"
                ? "ring-2 ring-rose-500 border-rose-300 bg-rose-50/70 shadow-sm"
                : "border-slate-200/80 bg-white hover:bg-slate-50/70"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                <HeartPulse size={18} />
              </div>
              <span className="text-xs font-bold text-rose-800 bg-rose-100/70 px-2 py-0.5 rounded-full">
                {withDisease.percent}%
              </span>
            </div>
            <div className="text-xl font-extrabold text-slate-900">
              {withDisease.count}
              <span className="text-xs font-normal text-slate-400 ml-1">colaboradores</span>
            </div>
            <h4 className="text-xs font-bold text-slate-800 mt-1">
              {withDisease.label}
            </h4>
            <p className="text-[11px] text-slate-500 mb-2">
              {withDisease.sublabel}
            </p>
            <div className="text-[10px] text-rose-800 font-medium bg-rose-50/80 p-2 rounded-lg border border-rose-100 leading-snug">
              {withDisease.action}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
